import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
  inject,
  TemplateRef,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import {
  DiffEditorComponent,
  DiffEditorModel,
  EditorComponent,
} from 'ngx-monaco-editor-v2';
import { ConfirmationDialogComponent } from '@nimmsta/ng-commons-lib/confirmation-dialog';
import {
  AppDialogRef,
  AppDialogService,
} from '@nimmsta/ng-commons-lib/services/app-dialog';
import { ErrorComponent } from '@nimmsta/ng-commons-lib/error';
import { HasUnsavedChanges } from '@nimmsta/ng-commons-lib/auth';
import {
  Workflow,
  WorkflowStatus,
  WorkflowDefinitionDraftRequest,
  WorkflowDefinitionType,
  WorkflowHistoryEntry,
  WorkflowDefinitionDraftResponse,
  PublishWorkflowDefinitionRequest,
} from '@nimmsta/ng-commons-lib/models';
import { WorkflowService } from '@nimmsta/ng-commons-lib/services/workflow';
import { HeaderBarComponent } from '@nimmsta/ng-commons-lib/header-bar';
import { LoadingIndicatorComponent } from '@nimmsta/ng-commons-lib/loading-indicator';
import { ReactiveFormsModule, FormsModule, FormControl } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { LaddaModule } from 'angular2-ladda';
import { ActivatedRoute, Router } from '@angular/router';
import {
  MatButtonToggle,
  MatButtonToggleGroup,
} from '@angular/material/button-toggle';
import { MatIcon } from '@angular/material/icon';
import { HistoryDropdownComponent } from './workflow-history-entry-selection.component';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';

_('error.workflow.invalid_json');
_('error.workflow.no_definition');

@Component({
  selector: 'app-workflow-definition',
  styleUrls: ['./workflow-definition.component.scss'],
  templateUrl: './workflow-definition.component.html',
  imports: [
    EditorComponent,
    ReactiveFormsModule,
    FormsModule,
    ErrorComponent,
    TranslatePipe,
    LaddaModule,
    HeaderBarComponent,
    LoadingIndicatorComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    DiffEditorComponent,
    MatIcon,
    HistoryDropdownComponent,
  ],
})
export class WorkflowDefinitionComponent
  implements HasUnsavedChanges, OnInit, AfterViewInit
{
  @ViewChild(HistoryDropdownComponent)
  historyDropdown!: HistoryDropdownComponent;

  private workflowService = inject(WorkflowService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private modalService = inject(AppDialogService);
  private dialogConfig = inject(DynamicDialogConfig<{ workflow: Workflow }>, {
    optional: true,
  });

  workflow!: Workflow;

  loading = true;

  saving = false;

  definition?: string;

  changesChecked = false;
  changesCheckedError = false;

  private originalDraft?: string;
  private originalDraftId?: string;

  draft?: string;

  commitMessage?: string;
  commitMessageError = false;

  editorCtrl = new FormControl<string | undefined>('');

  selected: 'draft' | 'currentVersion' | 'diff' = 'draft';

  currentDefinitionModel: DiffEditorModel = { language: 'json', code: '' };
  draftModel: DiffEditorModel = { language: 'json', code: '' };

  error?: HttpErrorResponse;

  @ViewChild('publishConfirmationModal', { static: true })
  publishConfirmationModal!: TemplateRef<unknown>;
  modalRef?: AppDialogRef;

  @ViewChild('lineWrap') lineWrap!: ElementRef;

  LINE_WRAP_KEY = 'line_wrap';

  lineWrapBoolean = localStorage.getItem(this.LINE_WRAP_KEY) === 'true';

  @ViewChild('editor') editor!: EditorComponent;

  editorOptions = {
    automaticLayout: true,
    theme: 'vs-dark',
    language: 'json',
    fontSize: '14',
    readOnly: false,
    wordWrap: 'on',
    scrollbar: {
      alwaysConsumeMouseWheel: false,
    },
  };

  diffEditorOptions = {
    automaticLayout: true,
    theme: 'vs-dark',
    language: 'json',
    fontSize: '14',
    wordWrap: 'on',
    readOnly: true,
    renderSideBySide: true,
    scrollbar: {
      alwaysConsumeMouseWheel: false,
    },
  };

  @HostListener('window:beforeunload', ['$event'])
  preventTabClose(event: BeforeUnloadEvent) {
    console.log('Preventing tab close');
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }

  hasUnsavedChanges(): boolean {
    return this.draft !== this.originalDraft && this.draft !== this.definition; // if draft was not saved or published
  }

  ngOnInit(): void {
    if (!localStorage.getItem(this.LINE_WRAP_KEY)) {
      localStorage.setItem(this.LINE_WRAP_KEY, this.lineWrapBoolean.toString());
    }
    this.editorCtrl.valueChanges.subscribe((v) => {
      if (this.selected === 'draft') {
        this.draft = v ?? '';
        this.changesChecked = false;
      }
    });
    this.workflow =
      this.dialogConfig?.data?.workflow ??
      this.router.getCurrentNavigation()?.extras.state?.['workflow'];
    if (!this.workflow) {
      const workflowId = this.route.snapshot.paramMap.get('id');
      if (workflowId) {
        this.workflowService.get(workflowId).subscribe({
          next: (workflow: Workflow) => {
            this.workflow = workflow;
            this.loadDefinition();
          },
          error: (error: HttpErrorResponse) => {
            this.error = error;
          },
        });
      }
    } else {
      this.loadDefinition();
    }
  }

  loadHistoryEntry(workflowHistoryEntry: WorkflowHistoryEntry) {
    this.workflowService
      .getDefinitionFromHistory(this.workflow, workflowHistoryEntry.id)
      .subscribe({
        next: (response: string) => {
          this.draft = JSON.stringify(response, null, 2);
          this.originalDraft = this.draft;
          this.originalDraftId = workflowHistoryEntry.id;
          this.saving = false;
          this.commitMessage = undefined;
          this.onSelectionChange('draft');
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.saving = false;
        },
      });
  }

  checkChanges() {
    this.changesChecked = true;
    this.changesCheckedError = false;
    this.commitMessageError = !this.commitMessage;
    this.onSelectionChange('diff');
  }

  private loadDefinition(): void {
    // load published definition
    this.workflowService.getDefinition(this.workflow).subscribe({
      next: (definition: string) => {
        this.definition = JSON.stringify(definition, null, 2);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.loading = false;
      },
    });

    // load draft definition
    this.workflowService.getDefinitionDraft(this.workflow).subscribe({
      next: (draftResponse: WorkflowDefinitionDraftResponse) => {
        this.draft = JSON.stringify(draftResponse.definition, null, 2);
        this.originalDraft = this.draft;
        this.originalDraftId = draftResponse.definitionHistoryId;
        this.editorCtrl.setValue(this.draft);
        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.error = error;
        this.loading = false;
      },
    });
  }

  back(): void {
    if (this.hasUnsavedChanges()) {
      const dialogRef = this.modalService.open<
        ConfirmationDialogComponent,
        unknown,
        boolean
      >(ConfirmationDialogComponent);
      dialogRef.closed.subscribe((confirmed) => {
        if (confirmed === true) {
          this.router.navigate(['/workflows']);
        }
      });
    } else {
      this.router.navigate(['/workflows']);
    }
  }

  onSelectionChange(selection: 'draft' | 'currentVersion' | 'diff') {
    this.selected = selection;
    if (selection === 'diff') {
      this.currentDefinitionModel.code = this.definition ?? '';
      this.draftModel.code = this.draft ?? '';
    } else {
      this.editorCtrl.setValue(
        this.selected === 'draft' ? this.draft : this.definition,
        { emitEvent: false },
      );
      if (selection !== 'draft') {
        this.editorCtrl.disable({ emitEvent: false });
        this.editorOptions.readOnly = true;
      } else {
        this.editorCtrl.enable({ emitEvent: false });
        this.editorOptions.readOnly = false;
      }
      this.editor.options = this.editorOptions;
    }
  }

  ngAfterViewInit(): void {
    this.lineWrapBoolean = localStorage.getItem(this.LINE_WRAP_KEY) === 'true';
    this.editorOptions.wordWrap = this.lineWrapBoolean ? 'on' : 'off';

    this.lineWrap.nativeElement.checked = this.lineWrapBoolean;
    this.lineWrap.nativeElement.addEventListener('change', () => {
      this.lineWrapBoolean = this.lineWrap.nativeElement.checked;
      localStorage.setItem(this.LINE_WRAP_KEY, this.lineWrapBoolean.toString());
      this.editorOptions.wordWrap = this.lineWrapBoolean ? 'on' : 'off';
      this.editor.options = this.editorOptions;
    });
  }

  saveDefinitionDraft(): void {
    if (this.draft && this.commitMessage) {
      this.commitMessageError = false;
      try {
        JSON.parse(this.draft);
        const definitionRequest: WorkflowDefinitionDraftRequest = {
          definition: this.draft,
          commitMessage: this.commitMessage,
        };
        this.saving = true;
        this.workflowService
          .setDefinitionDraft(this.workflow, definitionRequest)
          .subscribe({
            next: (response: WorkflowDefinitionDraftResponse) => {
              this.originalDraft = this.draft;
              this.originalDraftId = response.definitionHistoryId;
              this.saving = false;
              this.commitMessage = undefined;
              this.historyDropdown.clearSelection();
            },
            error: (error: HttpErrorResponse) => {
              this.error = error;
              this.saving = false;
            },
          });
      } catch (error) {
        this.error = new HttpErrorResponse({
          error: {
            message: 'error.workflow.invalid_json',
            details: error,
          },
          status: 400,
        });
      }
    } else if (!this.commitMessage) {
      this.commitMessageError = true;
    } else {
      this.error = new HttpErrorResponse({
        error: { error: 'error.workflow.no_definition' },
        status: 400,
      });
    }
  }

  publishButtonPressed(): void {
    if (
      this.draft === this.originalDraft &&
      this.commitMessage &&
      this.changesChecked
    ) {
      this.definition = this.draft;
      this.modalRef = this.modalService.open(this.publishConfirmationModal);
    } else if (!this.commitMessage) {
      this.commitMessageError = true;
    } else if (!this.changesChecked) {
      this.changesCheckedError = true;
    }
  }

  publishDraftAsDefinition(): void {
    if (
      this.draft === this.originalDraft &&
      this.commitMessage &&
      this.changesChecked &&
      this.originalDraftId
    ) {
      const request: PublishWorkflowDefinitionRequest = {
        commitMessage: this.commitMessage,
        draftIdToPublish: this.originalDraftId,
      };
      this.saving = true;
      this.workflowService.publishDefinition(this.workflow, request).subscribe({
        next: (newDefinition: string) => {
          this.definition = JSON.stringify(newDefinition, null, 2);
          this.currentDefinitionModel.code = this.definition;
          this.saving = false;
          this.commitMessage = undefined;
          this.commitMessageError = false;
          this.modalRef?.close();
          this.onSelectionChange('currentVersion');
          this.historyDropdown.clearSelection();
        },
        error: (error: HttpErrorResponse) => {
          this.error = error;
          this.saving = false;
          this.modalRef?.close();
        },
      });
    } else if (!this.commitMessage) {
      this.commitMessageError = true;
    } else if (!this.changesChecked) {
      this.changesCheckedError = true;
    }
  }

  isActive(): boolean {
    return this.workflow.status === WorkflowStatus.ACTIVE;
  }

  protected readonly WorkflowDefinitionType = WorkflowDefinitionType;
}
